import { useAtomValue, useSetAtom } from "jotai";
import { Info, RefreshCw, Loader} from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { isInfoPacketPayload } from "@/api/model/current-lic-packet";
import type { Device } from "@/api/model/device";
import Button from "@/components/ui/Button";
import { latestLICPacketByDeviceAndTypeAtom, licStateByDeviceIdAtom } from "@/store";
import { Modal, useToast } from ".";
import { useLICDepurationData } from "@/hooks/useLICDepurationData";
import { currentLICPacketsAtom, fetchCurrentLICPacketsAtom, fetchLicStatesAtom } from "@/store/data";

interface LICDebugModalProps {
  trigger: (open: () => void) => React.ReactNode;
  device: Device;
  enablePolling?: boolean;
}

function LICDebugModal({ trigger, device, enablePolling = false }: LICDebugModalProps) {
  const { showSuccess, showError } = useToast();
  const latestLICPacketByDeviceAndType = useAtomValue(latestLICPacketByDeviceAndTypeAtom);
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    deviceMessageRequest,
    currentPacket,
    isPolling,
    start,
    TesteIniciar
  } = useLICDepurationData({
    deviceId: device.id,
    pollingInterval: 3000,
    timeout: 30000
  });

  const latestInfoPacket = useMemo(
    () => latestLICPacketByDeviceAndType(device.id, "info"),
    [device.id, latestLICPacketByDeviceAndType]
  );
  // Use real-time packet from hook or fallback to global state
  useEffect(() => {
    start();
    TesteIniciar();
  },[ ])
  const activePacket = currentPacket || latestInfoPacket;

  // Atoms
  const licState = useAtomValue(licStateByDeviceIdAtom)(device.id);
  const currentPackets = useAtomValue(currentLICPacketsAtom);
  const fetchLicStates = useSetAtom(fetchLicStatesAtom);
  const fetchCurrentLICPackets = useSetAtom(fetchCurrentLICPacketsAtom);

  const infoData = useMemo(() => {
    if (!activePacket?.payload_data) return null;
    if (isInfoPacketPayload(activePacket.payload_data)) {
      return activePacket.payload_data;
    }
    return null;
  }, [activePacket]);

  const syncChecks = [
  {
    id: "config",
    name: "Configuração",
    serverValue: licState?.current_config_timestamp,
    deviceValue: infoData?.info.config_id,
    lastUpdate: licState?.last_config_request
  },
  {
    id: "scheduling",
    name: "Programação",
    serverValue: licState?.current_scheduling_timestamp,
    deviceValue: infoData?.info.scheduling_id?.toString(),
    lastUpdate: licState?.last_scheduling_request
  },
  {
    id: "devices",
    name: "Dispositivos",
    serverValue: licState?.current_devices_timestamp,
    deviceValue: infoData?.info.devices_id?.toString(),
    lastUpdate: licState?.last_devices_request
  },
  {
    id: "devScheduling",
    name: "Programação de Dispositivos",
    serverValue: licState?.current_dev_scheduling_timestamp,
    deviceValue: infoData?.info.dev_scheduling_id?.toString(),
    lastUpdate: licState?.last_dev_scheduling_request
  },
  {
    id: "automation",
    name: "Automação",
    serverValue: licState?.current_automation_timestamp,
    deviceValue: infoData?.info.automation_id?.toString(),
    lastUpdate: licState?.last_automation_request
  },
];
  const mapPayloadType: Record<string, string> = {
  "raw": "RAW",
  "info": "INFO",
  "scheduling_report": "RELATÓRIO DE AGENDAMENTO",
  "status": "STATUS",
  "automation_report": "RELATÓRIO DE AUTOMAÇÃO",
  "ack": "ACK",
};

  const handleOpen = () => {
    setIsOpen(true);
    if (enablePolling) {
      TesteIniciar();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsRefreshing(false);
  };

  const formatDate = (dateString: string, showHour: boolean = false) => {
    try {
      return new Date(dateString).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: showHour ? 'short' : undefined,
      });
    } catch {
      return dateString;
    }
  };

  const getDateByTimestamp = (timestamp: number)=>{
    if (!timestamp) return null;
    const ts = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
    const date = new Date(ts * 1000);
    return date.toISOString();
  }

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      try {
        TesteIniciar();
      } catch (err) {
        console.log("TesteIniciar falhou, continuando...", err);
      }

      await Promise.all([
        fetchLicStates(),
        fetchCurrentLICPackets()
      ]);
      
      showSuccess({
        message: "Dados atualizados com sucesso",
      });
    } catch (error) {
      showError({
        message: "Falha ao recarregar dados",
      });
    } finally {
      setIsRefreshing(false);
    }
};

  const getPacketAge = (packetDate: string): string => {
    if (!packetDate) return "Desconhecido";
    
    try {
      const packetTime = new Date(packetDate).getTime();
      const now = Date.now();
      const diffMs = now - packetTime;
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      
      if (diffMinutes < 1) return "Agora mesmo";
      if (diffMinutes < 60) return `${diffMinutes} min atrás`;
      if (diffHours < 24) return `${diffHours}h atrás`;
      return `${Math.floor(diffHours / 24)} dias atrás`;
    } catch {
      return "Data inválida";
    }
  };

  return (
    <>
      {trigger(handleOpen)}

      <Modal isOpen={isOpen} onClose={handleClose} title="Informações do CODEK" size="lg">
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Info className="w-5 h-5 text-blue-600" />
                <div className="flex flex-col mb-1">
                  <h4 className="font-medium text-black-800">Pacote INFO Atual</h4>
                  {activePacket && (
                    <p className="text-xs text-gray-500">
                      Idade: {getPacketAge(activePacket.date_created)}
                    </p>
                  )}
                </div>
              </div>
              <div className={`
                  flex flex-row items-center space-x-2 px-2 py-1 rounded-full
                  transition-colors duration-200
                  ${
                    isPolling 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : 'bg-green-100 text-green-800'
                  }`}>
                <span className="text-xs font-medium ">
                  {isPolling ? 'Atualizando' : 'Atualizado'}
                </span>
                {isPolling && <Loader size={16} className="animate-[spin_3s_linear_infinite] opacity-80"/>}
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex flex-row justify-between mb-6 items-center">
                <h3 className="font-semibold text-gray-800">{device.identifier}</h3>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRefreshAll}
                    disabled={isRefreshing}
                    icon={<RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />}
                    >
                    Recarregar
                </Button>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">ID do Registro:</span>
                <span className="text-sm font-mono font-medium">
                  {activePacket?.id || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Data do Registro:</span>
                <span className="text-sm">
                  {activePacket?.packet_date 
                    ? formatDate(activePacket.packet_date, true)
                    : "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Última Atualização:</span>
                <span className="text-sm">
                  {activePacket?.date_created
                    ? formatDate(activePacket.date_created, true)
                    : "N/A"}
                </span>
              </div>
              {deviceMessageRequest && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Solicitação Criada:</span>
                  <span className="text-sm">
                    {formatDate(deviceMessageRequest.date_created, true)}
                  </span>
                </div>
              )}
            </div>
          </div>

            {/** Seção de Sincronização Expandida */}
          <div className="border border-gray-300 rounded-lg">
            <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-300">
              <h4 className="font-medium text-gray-800">Sincronização Servidor - Dispositivo</h4>
            </div>

              <div className="p-4 space-y-3 grid grid-cols-2">
                  {syncChecks.map((check) => {
                    const isSynced =
                      check.serverValue !== undefined &&
                      check.deviceValue !== undefined &&
                      String(check.serverValue) === String(check.deviceValue);

                    return (
                      <div key={check.id} className="justify-between flex flex-col border-b border-gray-300 pb-3 last:border-0 last:pb-0">
                        <div className="flex flex-col justify-start items-start mb-1">
                          <span className="text-sm font-medium text-gray-700 min-h-[2.5rem]">{check.name}</span>
                          {check.lastUpdate  &&  <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              isSynced ? 'bg-green-500' : 'bg-red-500'
                            }`}
                            aria-label={isSynced ? "Sincronizado" : "Dessincronizado"}
                            />
                            <span className={`text-xs font-medium ${
                              isSynced ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {isSynced ? 'Sincronizado' : 'Dessincronizado'}
                            </span>
                          </div>}
                        </div>
              
                    {!isSynced && <div className="gap-2 text-xs text-gray-600">
                      <div>
                        <span className="font-medium">Servidor:</span>
                        <div className="font-mono">{check.serverValue ? formatDate(getDateByTimestamp(check.serverValue)!, true) 
                                                    : "N/A"}</div>
                      </div>
                      <div>
                        <span className="font-medium">Dispositivo:</span>
                        <div className="font-mono">{check.deviceValue && formatDate(getDateByTimestamp(parseInt(check.deviceValue as string, 10))!, true) || "N/A"}</div>
                      </div>
                    </div>}
              
                    <div className="text-xs text-gray-500 mt-1">
                      Última atualização: {check.lastUpdate ? formatDate(getDateByTimestamp(check.lastUpdate)!, true) 
                                          : "N/A"}
                    </div>
                  </div>
                  )})}
                </div>
          </div>

          <div className="space-y-4">
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-300">
                <h4 className="font-medium text-gray-800">Dados do Payload</h4>
              </div>
              
              <div className="p-4 space-y-4">
                {currentPackets.map((packet, index) => {
                  const translatedType = mapPayloadType[packet.payload_type] || packet.payload_type;

                  return (
                  <div key={`${index}`}>
                    <h4 className="font-medium text-gray-800">
                      {translatedType} - {formatDate(getDateByTimestamp(packet.payload_data.id as number) as string, true)}</h4>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relatório de Saúde - Pacote Ativo
                    </label>
                    {packet.packet_date ? (
                      <div className="bg-gray-50 border border-gray-300 rounded-lg overflow-hidden">
                        <pre className="text-sm p-4 overflow-auto max-h-64">
                          {JSON.stringify(packet.payload_data, null, 2)}
                        </pre>
                      </div>
                    ) : (
                      <div className="text-gray-500 italic text-sm py-3">
                        Nenhum dado disponível
                      </div>
                    )}
                </div>)})}
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button 
              variant="secondary" 
              onClick={handleClose} 
              className="flex-1"
            >
              Fechar
            </Button>
            
          </div>
        </div>
      </Modal>
    </>
  );
}

export default LICDebugModal;